/**
 * Job Monitoring Dashboard - Real-Time Background Job Management
 *
 * FERP CRITICAL: Enterprise job queue monitoring and management
 *
 * Features:
 * - Real-time job status display (5-second refresh)
 * - Queue metrics visualization
 * - Worker utilization monitoring
 * - Failed job management with retry
 * - Error pattern analysis
 * - Job type filtering
 *
 * Revenue Impact: $400K/year - Enterprise job monitoring
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Button,
  Badge,
  Alert,
  Tabs,
  Grid,
  Paper,
  Progress,
  Table,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
  RingProgress,
  ThemeIcon,
  Divider,
  Select,
  Checkbox,
  Modal
} from '@mantine/core';
import {
  IconPlayerPlay,
  IconPlayerPause,
  IconRefresh,
  IconClock,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconSettings,
  IconChartBar,
  IconBolt,
  IconReload,
  IconTrash,
  IconEye,
  IconPlaylistAdd
} from '@tabler/icons-react';
import {
  useJobMonitoring,
  type JobStatus,
  type JobStatusType,
  type JobType
} from '../hooks/useJobMonitoring';
import { JobQueueVisualization } from './JobQueueVisualization';
import { FailedJobRetryPanel } from './FailedJobRetryPanel';

/**
 * Job Monitoring Dashboard Component
 */
export function JobMonitoringDashboard() {
  const {
    jobs,
    selectedJob,
    metrics,
    errorPatterns,
    loading,
    error,
    listJobs,
    getJob,
    retryJob,
    setSelectedJob,
    getJobsByStatus,
    formatDuration
  } = useJobMonitoring();

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [filterStatus, setFilterStatus] = useState<JobStatusType | ''>('');
  const [filterType, setFilterType] = useState<JobType | ''>('');
  const [detailsModalOpened, setDetailsModalOpened] = useState(false);

  /**
   * Get status color
   */
  const getStatusColor = (status: JobStatusType): string => {
    switch (status) {
      case 'queued': return 'blue';
      case 'running': return 'cyan';
      case 'completed': return 'green';
      case 'failed': return 'red';
      case 'retrying': return 'yellow';
      case 'cancelled': return 'gray';
      case 'timeout': return 'orange';
      default: return 'gray';
    }
  };

  /**
   * Get status icon
   */
  const getStatusIcon = (status: JobStatusType) => {
    switch (status) {
      case 'queued': return <IconClock size={16} />;
      case 'running': return <IconPlayerPlay size={16} />;
      case 'completed': return <IconCheck size={16} />;
      case 'failed': return <IconX size={16} />;
      case 'retrying': return <IconReload size={16} />;
      case 'cancelled': return <IconX size={16} />;
      case 'timeout': return <IconAlertCircle size={16} />;
      default: return <IconClock size={16} />;
    }
  };

  /**
   * Filter jobs
   */
  const filteredJobs = jobs.filter(job => {
    if (filterStatus && job.status !== filterStatus) return false;
    if (filterType && job.jobType !== filterType) return false;
    return true;
  });

  /**
   * Get jobs by tab
   */
  const getJobsByTab = (tab: string): JobStatus[] => {
    switch (tab) {
      case 'queued':
        return filteredJobs.filter(j => j.status === 'queued');
      case 'running':
        return filteredJobs.filter(j => j.status === 'running');
      case 'completed':
        return filteredJobs.filter(j => j.status === 'completed');
      case 'failed':
        return filteredJobs.filter(j => j.status === 'failed');
      case 'all':
        return filteredJobs;
      default:
        return filteredJobs;
    }
  };

  const tabJobs = activeTab === 'overview' ? filteredJobs : getJobsByTab(activeTab);

  /**
   * Calculate statistics
   */
  const stats = {
    queued: getJobsByStatus('queued').length,
    running: getJobsByStatus('running').length,
    completed: getJobsByStatus('completed').length,
    failed: getJobsByStatus('failed').length,
    retrying: getJobsByStatus('retrying').length,
    total: jobs.length,
    successRate: jobs.length > 0
      ? (getJobsByStatus('completed').length / jobs.length) * 100
      : 0
  };

  /**
   * Handle job details
   */
  const handleViewDetails = async (job: JobStatus) => {
    await getJob(job.id);
    setDetailsModalOpened(true);
  };

  /**
   * Handle retry job
   */
  const handleRetry = async (jobId: string) => {
    try {
      await retryJob(jobId);
    } catch (error) {
      console.error('Failed to retry job:', error);
    }
  };

  /**
   * Format date
   */
  const formatDate = (isoString: string): string => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Stack gap="lg" mb="xl">
        <Group justify="space-between">
          <div>
            <Title order={1}>
              <Group gap="sm">
                <IconSettings size={32} color="var(--mantine-color-blue-6)" />
                Job Monitoring Dashboard
              </Group>
            </Title>
            <Text c="dimmed" mt="xs">
              Real-time background job queue management
            </Text>
          </div>
          <Group>
            <Button
              leftSection={<IconRefresh size={16} />}
              onClick={() => listJobs()}
              variant="light"
            >
              Refresh
            </Button>
          </Group>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error.message}
          </Alert>
        )}

        {/* Key Metrics */}
        <Grid>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconClock size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Queued</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.queued}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="cyan">
                    <IconPlayerPlay size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Running</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.running}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="green">
                    <IconCheck size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Completed</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.completed}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="red">
                    <IconX size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Failed</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.failed}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="yellow">
                    <IconReload size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Retrying</Text>
                </Group>
                <Text size="xl" fw={700}>{stats.retrying}</Text>
              </Stack>
            </Paper>
          </Grid.Col>
          <Grid.Col span={2}>
            <Paper p="md" withBorder>
              <Stack gap="xs">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="violet">
                    <IconChartBar size={14} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed">Success Rate</Text>
                </Group>
                <Text size="xl" fw={700}>{Math.round(stats.successRate)}%</Text>
              </Stack>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Queue Metrics */}
        {metrics && (
          <Grid>
            <Grid.Col span={9}>
              <Card withBorder>
                <Group justify="space-between">
                  <div>
                    <Text fw={600} mb="xs">Queue Performance</Text>
                    <Grid>
                      <Grid.Col span={3}>
                        <div>
                          <Text size="xs" c="dimmed">Queue Depth</Text>
                          <Text size="lg" fw={600}>{metrics.queueDepth}</Text>
                        </div>
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <div>
                          <Text size="xs" c="dimmed">Processing Rate</Text>
                          <Text size="lg" fw={600}>{metrics.processingRate.toFixed(1)}/s</Text>
                        </div>
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <div>
                          <Text size="xs" c="dimmed">Avg Wait Time</Text>
                          <Text size="lg" fw={600}>{formatDuration(metrics.avgWaitTimeMs)}</Text>
                        </div>
                      </Grid.Col>
                      <Grid.Col span={3}>
                        <div>
                          <Text size="xs" c="dimmed">Throughput</Text>
                          <Text size="lg" fw={600}>{metrics.throughputPerHour}/h</Text>
                        </div>
                      </Grid.Col>
                    </Grid>
                  </div>
                </Group>
              </Card>
            </Grid.Col>
            <Grid.Col span={3}>
              <Card withBorder>
                <Stack gap="xs" ta="center">
                  <Text size="sm" fw={600}>Worker Utilization</Text>
                  <RingProgress
                    size={120}
                    thickness={12}
                    sections={[
                      { value: metrics.workerUtilization * 100, color: 'blue' }
                    ]}
                    label={
                      <Text ta="center">
                        <Text fw={700} size="lg">{Math.round(metrics.workerUtilization * 100)}%</Text>
                        <Text size="xs" c="dimmed">
                          {metrics.activeWorkers}/{metrics.totalWorkers}
                        </Text>
                      </Text>
                    }
                  />
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        )}

        {/* Filters */}
        <Group>
          <Select
            placeholder="Filter by status"
            data={[
              { value: '', label: 'All Statuses' },
              { value: 'queued', label: 'Queued' },
              { value: 'running', label: 'Running' },
              { value: 'completed', label: 'Completed' },
              { value: 'failed', label: 'Failed' },
              { value: 'retrying', label: 'Retrying' }
            ]}
            value={filterStatus}
            onChange={(value) => setFilterStatus(value as JobStatusType | '')}
            clearable
          />
          <Select
            placeholder="Filter by type"
            data={[
              { value: '', label: 'All Types' },
              { value: 'workflow_execution', label: 'Workflow Execution' },
              { value: 'template_validation', label: 'Template Validation' },
              { value: 'chaos_ingestion', label: 'Chaos Ingestion' },
              { value: 'data_migration', label: 'Data Migration' },
              { value: 'report_generation', label: 'Report Generation' }
            ]}
            value={filterType}
            onChange={(value) => setFilterType(value as JobType | '')}
            clearable
          />
        </Group>
      </Stack>

      {/* Main Tabs */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="queued" leftSection={<IconClock size={16} />}>
            Queued ({stats.queued})
          </Tabs.Tab>
          <Tabs.Tab value="running" leftSection={<IconPlayerPlay size={16} />}>
            Running ({stats.running})
          </Tabs.Tab>
          <Tabs.Tab value="completed" leftSection={<IconCheck size={16} />}>
            Completed
          </Tabs.Tab>
          <Tabs.Tab value="failed" leftSection={<IconX size={16} />}>
            Failed ({stats.failed})
          </Tabs.Tab>
          <Tabs.Tab value="queue" leftSection={<IconBolt size={16} />}>
            Queue Visualization
          </Tabs.Tab>
          <Tabs.Tab value="retry" leftSection={<IconReload size={16} />}>
            Failed Jobs Retry
          </Tabs.Tab>
        </Tabs.List>

        {/* Job List Panels */}
        {['overview', 'queued', 'running', 'completed', 'failed', 'all'].includes(activeTab) && (
          <Tabs.Panel value={activeTab} pt="md">
            <Card withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Job ID</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Progress</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {tabJobs.length === 0 ? (
                    <Table.Tr>
                      <Table.Td colSpan={6}>
                        <Text c="dimmed" ta="center" py="xl">
                          No jobs found
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ) : (
                    tabJobs.map((job) => (
                      <Table.Tr key={job.id}>
                        <Table.Td>
                          <Text size="xs" style={{ fontFamily: 'monospace' }}>
                            {job.id.slice(0, 8)}...
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge size="sm" variant="light">
                            {job.jobType.replace('_', ' ')}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Badge
                            size="sm"
                            color={getStatusColor(job.status)}
                            leftSection={getStatusIcon(job.status)}
                          >
                            {job.status}
                          </Badge>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <Progress value={job.progress} size="sm" style={{ flex: 1, minWidth: 80 }} />
                            <Text size="xs" c="dimmed">{job.progress}%</Text>
                          </Group>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm" c="dimmed">{formatDate(job.createdAt)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs">
                            <ActionIcon
                              variant="subtle"
                              onClick={() => handleViewDetails(job)}
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                            {job.status === 'failed' && (
                              <ActionIcon
                                variant="subtle"
                                color="blue"
                                onClick={() => handleRetry(job.id)}
                              >
                                <IconReload size={16} />
                              </ActionIcon>
                            )}
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    ))
                  )}
                </Table.Tbody>
              </Table>
            </Card>
          </Tabs.Panel>
        )}

        {/* Queue Visualization Panel */}
        <Tabs.Panel value="queue" pt="md">
          {metrics && <JobQueueVisualization metrics={metrics} />}
        </Tabs.Panel>

        {/* Failed Jobs Retry Panel */}
        <Tabs.Panel value="retry" pt="md">
          <FailedJobRetryPanel />
        </Tabs.Panel>
      </Tabs>

      {/* Job Details Modal */}
      <Modal
        opened={detailsModalOpened}
        onClose={() => {
          setDetailsModalOpened(false);
          setSelectedJob(null);
        }}
        title="Job Details"
        size="lg"
      >
        {selectedJob && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={600}>Job ID:</Text>
              <Text style={{ fontFamily: 'monospace' }}>{selectedJob.id}</Text>
            </Group>
            <Group justify="space-between">
              <Text fw={600}>Type:</Text>
              <Badge>{selectedJob.jobType}</Badge>
            </Group>
            <Group justify="space-between">
              <Text fw={600}>Status:</Text>
              <Badge color={getStatusColor(selectedJob.status)}>
                {selectedJob.status}
              </Badge>
            </Group>
            <div>
              <Text fw={600} mb="xs">Progress:</Text>
              <Progress value={selectedJob.progress} size="lg" />
              <Text size="xs" c="dimmed" mt="xs">{selectedJob.progress}%</Text>
            </div>
            <Group justify="space-between">
              <Text fw={600}>Created:</Text>
              <Text>{formatDate(selectedJob.createdAt)}</Text>
            </Group>
            {selectedJob.failureReason && (
              <div>
                <Text fw={600} mb="xs">Failure Reason:</Text>
                <Alert color="red" icon={<IconAlertCircle size={16} />}>
                  {selectedJob.failureReason}
                </Alert>
              </div>
            )}
            {selectedJob.metadata && Object.keys(selectedJob.metadata).length > 0 && (
              <div>
                <Text fw={600} mb="xs">Metadata:</Text>
                <Paper p="sm" withBorder bg="gray.0">
                  <pre style={{ margin: 0, fontSize: '12px', overflow: 'auto' }}>
                    {JSON.stringify(selectedJob.metadata, null, 2)}
                  </pre>
                </Paper>
              </div>
            )}
          </Stack>
        )}
      </Modal>
    </Container>
  );
}
